// Katalog — /clients/kpi/katalog (M16 T10 adım 3, mockup 09). Firma genelinde ortak gösterge
// kütüphanesi: arama + kök kategori sekmeleri (sayaçlı) + "＋ Gösterge / ＋ Kategori" (kpi.manage) +
// "＋ Katalogdan Set Oluştur" (fork modalı, müşteri ön-seçimi kpi-store'dan). Satır → MetricModal (tek pencere: oluştur + düzenle).
// Kullanımdan kalkanlar yalnız anahtar açıkken listelenir. KPI-28: "Dosyadan içe aktar"
// (kpi.manage) → CatalogImportModal — müşterisiz, Excel/Word satırlarından gösterge tanımı.
// Finar rev-B 10/11: satırdan hızlı "Kullanımdan kaldır" (ConfirmDialog, aynı DELETE ucu); sette
// kullanılıyorsa 409 detayındaki veri setleri bağlantılarıyla bantta listelenir.
// Kırılım (spec §6.4): satır "Kırılıma dönüştür" + MetricModal "Yeni grup…" (use-catalog-breakdown);
// pencere türü shared satır türünden (kategori / grup / gösterge — kırılımlı toplam gösterge sayılır).
// Sektör paketleri (§5 Katalog): Paket süzgeci + rozetler (use-catalog-packages).
// Sürükle-bırak sıralama (rev-2 #4b/#12b/#14): iyimser + satır başına PATCH (use-catalog-reorder).

import { useCallback, useEffect, useMemo, useState } from "react";
import type { KpiMetric, KpiMetricRenameCodeResult, KpiMetricRequirement } from "@mahir/shared";
import { useSearchParams } from "react-router-dom";
import { ApiError } from "../../api/client";
import {
  createKpiMetric,
  deprecateKpiMetric,
  listKpiMetrics,
  updateKpiMetric,
  renameKpiMetricCode,
} from "../../api/kpi";
import { CatalogDimensionsButton } from "../../components/kpi/CatalogDimensionsButton";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { ErrorState, LoadingState } from "../../components/common/StateSurface";
import { CatalogImportModal } from "../../components/kpi/CatalogImportModal";
import { CatalogTable } from "../../components/kpi/CatalogTable";
import { ForkSetModal } from "../../components/kpi/ForkSetModal";
import type { FormulaServerError } from "../../components/kpi/FormulaEditor";
import { MetricErrorText } from "../../components/kpi/MetricInUseNotice";
import { KpiScreen } from "../../components/kpi/KpiScreen";
import { MetricModal } from "../../components/kpi/MetricModal";
import {
  KPI_ROOT_ORDER,
  btnGhost,
  btnPrimary,
  inputStyle,
} from "../../components/kpi/kpi-form-styles";
import { breakdownErrorText } from "../../lib/kpi-breakdown-copy";
import { catalogBreakdownView } from "../../lib/kpi-catalog-breakdown";
import { buildIndicatorTree, formatCyclePath, leafCountsByRoot } from "../../lib/kpi-set-editor";
import { KPI_CATALOG_IMPORT_COPY } from "../../lib/kpi-catalog-import-copy";
import { draftFromMetric, draftToUpsert, type MetricDraft } from "../../lib/kpi-metric-draft";
import { metricInUseSets } from "../../lib/kpi-metric-in-use";
import { KPI_COPY } from "../../lib/kpi-ui";
import { renameConfirmBody } from "../../lib/kpi-unit-copy";
import {
  ROLE_UI_COPY,
  SUPERADMIN_ONLY_HINT,
  canManageCatalogCategories,
  catalogNodeEditable,
} from "../../lib/role-ui";
import { useAuthStore } from "../../stores/auth-store";
import { useKpiStore } from "../../stores/kpi-store";
import { useCatalogBreakdown } from "./use-catalog-breakdown";
import { useCatalogPackages } from "./use-catalog-packages";
import { useCatalogReorder } from "./use-catalog-reorder";

type Pending =
  | { kind: "none" }
  | { kind: "create"; preset: { kind: "metric" | "category"; parentId: string | null } }
  | { kind: "edit"; id: string };

const EMPTY_PRESET = { kind: "metric" as const, parentId: null };

function toServerError(err: unknown): FormulaServerError {
  if (err instanceof ApiError) {
    const d = (err.details ?? {}) as Record<string, unknown>;
    return {
      // kırılım yerleşim kodları tek kaynaktan; bilinmeyen kod sunucu metniyle
      message: breakdownErrorText(err.code, err.message),
      cyclePath: err.code === "KPI_FORMULA_CYCLE" ? formatCyclePath(d.cycle) : null,
      formulaId: typeof d.formulaId === "string" ? d.formulaId : null,
      position: typeof d.position === "number" ? d.position : null,
      inUseSets: metricInUseSets(err.code, err.details),
    };
  }
  return { message: err instanceof Error ? err.message : "Kaydedilemedi" };
}

export function KpiCatalogPage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const canManage = user?.permissions["kpi.manage"] === true;
  // Finar rev. madde 12: kategori düğümleri (kök / çocuklu) yalnız `kpi.catalog.categories` ile
  const canManageCategories = canManageCatalogCategories(user?.permissions);
  const clientId = useKpiStore((s) => s.clientId);

  const [metrics, setMetrics] = useState<KpiMetric[] | null>(null);
  const [requirements, setRequirements] = useState<(KpiMetricRequirement & { metricId: string })[]>(
    [],
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showDeprecated, setShowDeprecated] = useState(false);
  const [search, setSearch] = useState("");
  const [rootId, setRootId] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>({ kind: "none" });
  const [draft, setDraft] = useState<MetricDraft>(() => draftFromMetric(null, [], EMPTY_PRESET));
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<FormulaServerError | null>(null);
  const [forkOpen, setForkOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  // rev-B 10 — satırdan hızlı kaldırma: onay bekleyen metrik + başarısızlık bandı (409 set listesi)
  const [quick, setQuick] = useState<KpiMetric | null>(null);
  const [quickError, setQuickError] = useState<FormulaServerError | null>(null);
  // kod değişikliği: dryRun sonucu + bekleyen taslak (onay penceresi)
  const [renamePending, setRenamePending] = useState<{
    draft: MetricDraft;
    result: KpiMetricRenameCodeResult;
  } | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await listKpiMetrics({ includeDeprecated: showDeprecated });
      setMetrics(res.metrics);
      setRequirements(res.requirements);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Katalog yüklenemedi");
    }
  }, [showDeprecated]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 5000);
    return () => clearTimeout(t);
  }, [flash]);

  const tree = useMemo(() => buildIndicatorTree(metrics ?? []), [metrics]);
  const view = useMemo(() => catalogBreakdownView(metrics ?? []), [metrics]);
  const breakdown = useCatalogBreakdown({
    metrics,
    reload: load,
    onFlash: setFlash,
    restoreDraft: setDraft,
    openMetric: (id) => openEdit(id),
  });
  // TODO-KPI-116: Veri Yönetimi "Kırılıma dönüştür" kısayolu → `?convert=<metricId>` sihirbazı açar
  const [searchParams, setSearchParams] = useSearchParams();
  const convertParam = searchParams.get("convert");
  useEffect(() => {
    if (!convertParam || !metrics || !canManage) return;
    const m = metrics.find((x) => x.id === convertParam);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("convert");
        return next;
      },
      { replace: true },
    );
    if (m) breakdown.openConvert(m, view.kindOf(m.id) === "category" ? "category" : "siblings");
  }, [convertParam, metrics, canManage, breakdown, view, setSearchParams]);
  const pkg = useCatalogPackages({ metrics });
  const reorder = useCatalogReorder({ metrics, setMetrics, reload: load });
  const rootCounts = useMemo(() => leafCountsByRoot(tree), [tree]);
  const roots = useMemo(() => {
    const list = tree.rows.filter((r) => r.depth === 0);
    const order = (code: string): number => {
      const i = (KPI_ROOT_ORDER as readonly string[]).indexOf(code);
      return i === -1 ? 99 : i;
    };
    return [...list].sort((a, b) => order(a.node.code) - order(b.node.code));
  }, [tree]);
  const totalLeaves = useMemo(() => tree.rows.filter((r) => !r.hasChildren).length, [tree]);
  const activeMetrics = useMemo(
    () => (metrics ?? []).filter((m) => m.status === "active"),
    [metrics],
  );
  const existingCodes = useMemo(() => new Set((metrics ?? []).map((m) => m.code)), [metrics]);

  const selected =
    pending.kind === "edit" ? ((metrics ?? []).find((m) => m.id === pending.id) ?? null) : null;
  const selectedEditable = selected
    ? catalogNodeEditable(selected.id, metrics ?? [], user?.permissions)
    : canManage;

  const originalDraft = (m: KpiMetric): MetricDraft =>
    draftFromMetric(
      m,
      requirements.filter((r) => r.metricId === m.id).map(({ metricId: _m, ...rest }) => rest),
      EMPTY_PRESET,
    );

  function openEdit(id: string): void {
    const m = (metrics ?? []).find((x) => x.id === id);
    if (!m) return;
    setFormError(null);
    setPending({ kind: "edit", id });
    setDraft(originalDraft(m));
  }

  function openCreate(kind: "metric" | "category"): void {
    setFormError(null);
    const preset = { kind, parentId: rootId };
    setPending({ kind: "create", preset });
    setDraft(draftFromMetric(null, [], preset));
  }

  async function save(d: MetricDraft, opts: { renameConfirmed?: boolean } = {}): Promise<void> {
    setBusy(true);
    setFormError(null);
    try {
      const current =
        pending.kind === "edit" ? (metrics ?? []).find((m) => m.id === pending.id) : undefined;
      const body = draftToUpsert(d, current ? originalDraft(current) : undefined);
      if (pending.kind === "edit") {
        const newCode = d.code.trim();
        if (current && newCode !== "" && newCode !== current.code) {
          // Kod değişiyor: önce etki (dryRun) → onay → yeniden adlandır (formüller yeniden yazılır)
          if (!opts.renameConfirmed) {
            const dry = await renameKpiMetricCode(pending.id, newCode, true);
            setRenamePending({ draft: d, result: dry.result });
            return;
          }
          const done = await renameKpiMetricCode(pending.id, newCode, false);
          const { code: _c, ...rest } = body;
          await updateKpiMetric(pending.id, rest);
          setFlash(
            `Kod ${done.result.from} → ${done.result.to}: ${done.result.catalogFormulas} katalog, ${done.result.setFormulas} veri seti formülü güncellendi`,
          );
          setPending({ kind: "none" });
          await load();
          return;
        }
        await updateKpiMetric(pending.id, body);
        // Pencere açık kalır; karşılaştırma tabanını kaydedilen taslağa çek — aksi halde kapatırken
        // "Kaydedilmemiş değişiklikler var" uyarısı çıkıyordu (Finar bulgusu, 2026-10-20).
        setDraft(d);
        setFlash("Gösterge kaydedildi");
      } else {
        const created = await createKpiMetric(body);
        setFlash(`"${created.label}" kataloğa eklendi`);
        setPending({ kind: "none" });
      }
      await load();
    } catch (err) {
      setFormError(toServerError(err));
    } finally {
      setBusy(false);
    }
  }

  async function deprecate(): Promise<void> {
    if (pending.kind !== "edit") return;
    setBusy(true);
    setFormError(null);
    try {
      const res = await deprecateKpiMetric(pending.id);
      setFlash(
        res.outcome === "deprecated"
          ? KPI_COPY.catalogDeprecate.deprecated(res.valueCount)
          : KPI_COPY.catalogDeprecate.deleted,
      );
      setPending({ kind: "none" });
      await load();
    } catch (err) {
      setFormError(toServerError(err));
    } finally {
      setBusy(false);
    }
  }

  async function quickDeprecate(m: KpiMetric): Promise<void> {
    setQuick(null);
    setQuickError(null);
    try {
      const res = await deprecateKpiMetric(m.id);
      setFlash(
        res.outcome === "deprecated"
          ? KPI_COPY.catalogDeprecate.deprecated(res.valueCount)
          : KPI_COPY.catalogDeprecate.deleted,
      );
      await load();
    } catch (err) {
      setQuickError(toServerError(err));
    }
  }

  const tabStyle = (on: boolean): React.CSSProperties => ({
    ...btnGhost,
    minHeight: 36,
    padding: "6px 12px",
    fontSize: "0.82rem",
    ...(on
      ? {
          background: "color-mix(in srgb, var(--color-primary) 10%, white)",
          color: "var(--color-primary)",
          borderColor: "var(--color-primary)",
          fontWeight: 600,
        }
      : {}),
  });

  return (
    <KpiScreen
      title={KPI_COPY.titles.katalog}
      subtitle="Firma genelinde ortak standart gösterge kütüphanesi. Müşteri veri setleri buradan kopyalanarak oluşturulur; katalogdaki değişiklik mevcut veri setlerini etkilemez."
      hideSet
      requireSet={false}
      headerRight={
        <button type="button" style={btnPrimary} onClick={() => setForkOpen(true)}>
          {KPI_COPY.emptyCreateSet}
        </button>
      }
    >
      {flash ? (
        <div
          role="status"
          className="text-sm rounded-lg px-3 py-2 mb-3"
          style={{
            background: "color-mix(in srgb, var(--color-success) 12%, white)",
            color: "var(--color-success)",
          }}
        >
          {flash}
        </div>
      ) : null}

      {reorder.band}

      {quickError ? (
        <div
          role="alert"
          data-testid="kpi-catalog-quick-error"
          className="text-sm rounded-lg px-3 py-2 mb-3 flex items-start gap-2"
          style={{
            background: "var(--color-warning-bg)",
            color: "var(--color-warning-text)",
            border: "1px solid var(--color-warning-border)",
          }}
        >
          <span className="flex-1" style={{ overflowWrap: "anywhere" }}>
            <MetricErrorText error={quickError} />
          </span>
          <button
            type="button"
            onClick={() => setQuickError(null)}
            style={{ textDecoration: "underline", minHeight: 24 }}
          >
            {KPI_COPY.catalogDeprecate.dismiss}
          </button>
        </div>
      ) : null}

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <input
          type="search"
          aria-label="Katalogda ara"
          placeholder="Katalogda ara…"
          style={{ ...inputStyle, width: 240 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {canManage ? <CatalogDimensionsButton onFlash={setFlash} /> : null}
        <div role="tablist" aria-label="Kök kategori" className="flex gap-1 flex-wrap">
          <button
            type="button"
            role="tab"
            aria-selected={rootId === null}
            style={tabStyle(rootId === null)}
            onClick={() => setRootId(null)}
          >
            Tümü ({totalLeaves})
          </button>
          {roots.map((r) => (
            <button
              key={r.node.id}
              type="button"
              role="tab"
              aria-selected={rootId === r.node.id}
              style={tabStyle(rootId === r.node.id)}
              onClick={() => setRootId(r.node.id)}
            >
              {r.node.label} ({rootCounts.get(r.node.id) ?? 0})
            </button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        <label
          className="flex items-center gap-2 text-xs"
          style={{ color: "var(--color-text-2)", minHeight: 36 }}
        >
          <input
            type="checkbox"
            checked={showDeprecated}
            onChange={(e) => setShowDeprecated(e.target.checked)}
          />
          Kullanımdan kalkanları göster
        </label>
        {canManage ? (
          <>
            <button type="button" style={btnGhost} onClick={() => openCreate("metric")}>
              ＋ Gösterge
            </button>
            <button
              type="button"
              style={{ ...btnGhost, ...(canManageCategories ? {} : { opacity: 0.5 }) }}
              disabled={!canManageCategories}
              title={canManageCategories ? undefined : SUPERADMIN_ONLY_HINT}
              onClick={() => openCreate("category")}
            >
              ＋ Kategori
            </button>
            {canManageCategories ? null : (
              <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
                {ROLE_UI_COPY.categoryHint}
              </span>
            )}
            <button type="button" style={btnGhost} onClick={() => setImportOpen(true)}>
              {KPI_CATALOG_IMPORT_COPY.button}
            </button>
          </>
        ) : null}
      </div>

      {pkg.bar}

      <div className="grid gap-4 items-start" style={{ gridTemplateColumns: "1fr" }}>
        <div
          className="rounded-xl"
          style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
        >
          {loadError ? (
            <ErrorState message={loadError} onRetry={() => void load()} />
          ) : metrics === null ? (
            <LoadingState />
          ) : (
            <CatalogTable
              metrics={metrics}
              rootId={rootId}
              search={search}
              selectedId={selected?.id ?? null}
              onSelect={openEdit}
              onDeprecate={canManage ? (m) => setQuick(m) : undefined}
              onConvert={canManage ? breakdown.openConvert : undefined}
              permissions={user?.permissions}
              onReorder={canManage ? reorder.onReorder : undefined}
              reorderBusy={reorder.busy}
              {...pkg.tableProps}
            />
          )}
        </div>
        {pending.kind !== "none" && metrics && !breakdown.groupOpen ? (
          <MetricModal
            kind={
              pending.kind === "create"
                ? pending.preset.kind
                : !selected
                  ? "metric"
                  : view.kindOf(selected.id) === "category"
                    ? "category"
                    : view.kindOf(selected.id) === "breakdown_group"
                      ? "group"
                      : "metric"
            }
            canManageCategories={canManageCategories}
            onNewGroup={canManage ? breakdown.openGroup : undefined}
            metric={selected}
            initialDraft={draft}
            catalog={metrics}
            canEdit={selectedEditable}
            lockedHint={canManage && !selectedEditable ? SUPERADMIN_ONLY_HINT : undefined}
            busy={busy}
            error={formError}
            onSave={(d) => void save(d)}
            onDeprecate={() => void deprecate()}
            onCancel={() => setPending({ kind: "none" })}
          />
        ) : null}
      </div>

      <ConfirmDialog
        open={quick !== null}
        title={quick ? KPI_COPY.catalogDeprecate.title(quick.label) : ""}
        body={KPI_COPY.catalogDeprecate.body}
        confirmLabel={KPI_COPY.catalogDeprecate.confirm}
        tone="danger"
        onConfirm={() => {
          if (quick) void quickDeprecate(quick);
        }}
        onCancel={() => setQuick(null)}
      />
      <ConfirmDialog
        open={renamePending !== null}
        title="Gösterge kodu değişecek"
        body={renamePending ? renameConfirmBody(renamePending.result) : ""}
        confirmLabel="Kodu değiştir ve kaydet"
        busy={busy}
        onConfirm={() => {
          const rp = renamePending;
          setRenamePending(null);
          if (rp) void save(rp.draft, { renameConfirmed: true });
        }}
        onCancel={() => setRenamePending(null)}
      />
      {breakdown.dialogs}
      <ForkSetModal
        open={forkOpen}
        initialClientId={clientId}
        metrics={activeMetrics}
        onClose={() => setForkOpen(false)}
      />
      {canManage ? (
        <CatalogImportModal
          open={importOpen}
          existingCodes={existingCodes}
          onClose={() => setImportOpen(false)}
          onImported={(r) => {
            setFlash(KPI_CATALOG_IMPORT_COPY.result.summary(r));
            void load();
          }}
        />
      ) : null}
    </KpiScreen>
  );
}
